import {
    renderApiStatusDetails,
    renderJsonDebugBlock,
    renderStatusTables,
} from './amp-monitoring-dom';

const initAmpMonitoringDashboard = () => {
    const statusContainer = document.getElementById('status-container');
    if (!statusContainer) {
        return;
    }

    const showCapacity = statusContainer.dataset.showCapacity === 'true';

    let countdown = 60;
    let timer = null;

    const initCountdown = () => {
        if (timer) {
            clearInterval(timer);
        }

        countdown = 60;
        updateCountdownDisplay();

        timer = setInterval(() => {
            countdown--;
            updateCountdownDisplay();

            if (countdown <= 0) {
                refreshData(false);
            }
        }, 1000);
    };

    const updateCountdownDisplay = () => {
        const countdownElement = document.getElementById('refresh-countdown');
        if (countdownElement) {
            countdownElement.textContent = `(Refreshing in ${countdown}s)`;
        }
    };

    const updateLastUpdated = () => {
        const now = new Date();
        const timeString = now.toLocaleTimeString();
        const lastUpdatedElement = document.getElementById('last-updated');
        if (lastUpdatedElement) {
            lastUpdatedElement.textContent = timeString;
        }
    };

    const updateStatistics = (data) => {
        if (!data) {
            return;
        }

        const totalInstancesEl = document.getElementById('total-instances');
        const runningInstancesEl = document.getElementById('running-instances');
        const stoppedInstancesEl = document.getElementById('stopped-instances');

        if (totalInstancesEl) {
            totalInstancesEl.textContent = data.statistics?.totalInstances || 0;
        }
        if (runningInstancesEl) {
            runningInstancesEl.textContent = data.statistics?.runningInstances || 0;
        }
        if (stoppedInstancesEl) {
            stoppedInstancesEl.textContent = (data.statistics?.totalInstances || 0) - (data.statistics?.runningInstances || 0);
        }

        if (showCapacity && data.capacity) {
            const totalCpuEl = document.getElementById('capacity-total-cpu');
            const totalMemEl = document.getElementById('capacity-total-mem');
            const freeCpuEl = document.getElementById('capacity-free-cpu');
            const freeMemEl = document.getElementById('capacity-free-mem');
            const assignedCpuEl = document.getElementById('capacity-assigned-cpu');
            const assignedMemEl = document.getElementById('capacity-assigned-mem');
            const cpuOverEl = document.getElementById('capacity-cpu-over');
            const memOverEl = document.getElementById('capacity-mem-over');

            if (totalCpuEl) {
                totalCpuEl.textContent = `${data.capacity.totalAvailableCpu || 0} cores`;
            }
            if (totalMemEl) {
                totalMemEl.textContent = `${data.capacity.totalAvailableMemoryGb || 0} GB`;
            }
            if (freeCpuEl) {
                freeCpuEl.textContent = `${data.capacity.freeCpu || 0} cores`;
            }
            if (freeMemEl) {
                freeMemEl.textContent = `${data.capacity.freeMemoryGb || 0} GB`;
            }
            if (assignedCpuEl) {
                assignedCpuEl.textContent = `${data.capacity.totalAssignedCpu || 0} assigned`;
            }
            if (assignedMemEl) {
                assignedMemEl.textContent = `${data.capacity.totalAssignedMemoryGb || 0} GB assigned`;
            }
            if (cpuOverEl) {
                cpuOverEl.textContent = `${data.capacity.cpuOverprovisionPercent || 0}%`;
            }
            if (memOverEl) {
                memOverEl.textContent = `${data.capacity.memoryOverprovisionPercent || 0}%`;
            }
        }
    };

    const updateStatusTable = (data) => {
        if (!data?.targets) {
            return;
        }

        const container = document.getElementById('status-container');
        if (!container) {
            return;
        }

        renderStatusTables(container, data);
    };

    const refreshData = (forceRefresh = false) => {
        const refreshBtn = document.getElementById('refresh-btn');
        let originalBtnText = '';

        if (refreshBtn) {
            originalBtnText = refreshBtn.textContent;
            refreshBtn.textContent = 'Refreshing...';
            refreshBtn.disabled = true;
        }

        const xhr = new XMLHttpRequest();
        const url = `/amp/api/global-status?refresh=${forceRefresh ? 'true' : 'false'}&capacity=${showCapacity ? 'true' : 'false'}&_=${Date.now()}`;

        xhr.open('GET', url, true);
        xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

        xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const response = JSON.parse(xhr.responseText);
                    if (response?.success) {
                        updateLastUpdated();
                        updateStatistics(response.data);
                        updateStatusTable(response.data);
                    }
                } catch (error) {
                    console.error('Error parsing response:', error);
                }
            }

            if (refreshBtn) {
                refreshBtn.textContent = originalBtnText;
                refreshBtn.disabled = false;
            }
        };

        xhr.onerror = function () {
            if (refreshBtn) {
                refreshBtn.textContent = originalBtnText;
                refreshBtn.disabled = false;
            }
        };

        xhr.send();
    };

    const refreshBtn = document.getElementById('refresh-btn');
    if (refreshBtn) {
        refreshBtn.addEventListener('click', () => {
            refreshData(true);
            initCountdown();
        });
    }

    initCountdown();
};

const initAmpMonitoringInstance = () => {
    const wrapper = document.querySelector('[data-amp-monitoring-instance="true"]');
    if (!wrapper) {
        return;
    }

    const apiEndpoint = wrapper.dataset.apiEndpoint;
    const connectionStatusElement = document.getElementById('connection-status');

    if (!apiEndpoint || !connectionStatusElement) {
        return;
    }

    let lastApiResponse = null;
    let pollingInterval = 1000;
    let isPolling = true;
    let pollingTimer = null;

    const addDebugMessage = (type, message, data = null) => {
        const logElement = document.getElementById('api-debug-log');
        if (!logElement) {
            return;
        }

        const timestamp = new Date().toLocaleTimeString();
        const msgElement = document.createElement('div');
        msgElement.className = type === 'error' ? 'text-red-500' : 'text-gray-800 dark:text-gray-300';
        msgElement.textContent = `[${timestamp}] ${message}`;

        if (data) {
            const dataStr = typeof data === 'object' ? JSON.stringify(data) : data.toString();
            const dataElement = document.createElement('div');
            dataElement.className = 'pl-4 text-gray-600 dark:text-gray-400 text-xs break-all';
            dataElement.textContent = dataStr.length > 500 ? `${dataStr.substring(0, 500)}...` : dataStr;
            msgElement.appendChild(dataElement);
        }

        logElement.prepend(msgElement);

        while (logElement.children.length > 50) {
            logElement.removeChild(logElement.lastChild);
        }
    };

    const updateApiStatus = (status, lastFetch = null) => {
        const statusElement = document.getElementById('api-status-details');
        if (!statusElement) {
            return;
        }

        const timestamp = lastFetch ? new Date(lastFetch).toLocaleTimeString() : 'N/A';

        renderApiStatusDetails(statusElement, {
            status,
            lastFetch: timestamp,
            isPolling,
            pollingInterval,
        });
    };

    const formatUptime = (uptimeValue) => {
        if (!uptimeValue) {
            return '0m 0s';
        }

        if (typeof uptimeValue === 'string' && uptimeValue.includes(':')) {
            const parts = uptimeValue.split(':');

            if (parts.length === 4) {
                const days = parseInt(parts[0], 10);
                const hours = parseInt(parts[1], 10);
                const minutes = parseInt(parts[2], 10);
                const seconds = parseInt(parts[3], 10);

                let uptimeStr = '';
                if (days > 0) uptimeStr += `${days}d `;
                if (hours > 0 || days > 0) uptimeStr += `${hours}h `;
                uptimeStr += `${minutes}m ${seconds}s`;

                return uptimeStr;
            }

            if (parts.length === 3) {
                const hours = parseInt(parts[0], 10);
                const minutes = parseInt(parts[1], 10);
                const seconds = parseInt(parts[2], 10);

                let uptimeStr = '';
                if (hours > 0) uptimeStr += `${hours}h `;
                uptimeStr += `${minutes}m ${seconds}s`;

                return uptimeStr;
            }

            return uptimeValue;
        }

        if (typeof uptimeValue === 'number' || !Number.isNaN(parseFloat(uptimeValue))) {
            const uptimeSeconds = Math.floor(parseFloat(uptimeValue));
            const days = Math.floor(uptimeSeconds / 86400);
            const hours = Math.floor((uptimeSeconds % 86400) / 3600);
            const minutes = Math.floor((uptimeSeconds % 3600) / 60);
            const seconds = uptimeSeconds % 60;

            let uptimeStr = '';
            if (days > 0) uptimeStr += `${days}d `;
            if (hours > 0 || days > 0) uptimeStr += `${hours}h `;
            uptimeStr += `${minutes}m ${seconds}s`;

            return uptimeStr;
        }

        return uptimeValue;
    };

    const processInstanceUpdates = (data) => {
        try {
            if (data.metrics) {
                if (data.metrics.cpu !== undefined) {
                    const cpuValueElement = document.getElementById('cpu-value');
                    const cpuProgressElement = document.getElementById('cpu-progress');
                    if (cpuValueElement && cpuProgressElement) {
                        cpuValueElement.textContent = `${data.metrics.cpu.toFixed(1)}%`;
                        cpuProgressElement.style.width = `${data.metrics.cpu}%`;
                    }
                }

                if (data.metrics.memory !== undefined) {
                    const memoryValueElement = document.getElementById('memory-value');
                    if (memoryValueElement) {
                        memoryValueElement.textContent = `${Math.round(data.metrics.memory)} MB`;
                    }
                }

                if (data.metrics.activeUsers !== undefined) {
                    const usersElement = document.getElementById('users-value');
                    if (usersElement) {
                        usersElement.textContent = data.metrics.activeUsers;
                    }
                }

                if (data.metrics.uptime !== undefined) {
                    const uptimeElement = document.getElementById('uptime-value');
                    if (uptimeElement) {
                        uptimeElement.textContent = formatUptime(data.metrics.uptime);
                    }
                }
            } else if (data.status && data.status.Metrics) {
                if (data.status.Metrics['CPU Usage']) {
                    const cpuValue = data.status.Metrics['CPU Usage'].Percent;
                    const cpuValueElement = document.getElementById('cpu-value');
                    const cpuProgressElement = document.getElementById('cpu-progress');
                    if (cpuValueElement && cpuProgressElement) {
                        cpuValueElement.textContent = `${cpuValue.toFixed(1)}%`;
                        cpuProgressElement.style.width = `${cpuValue}%`;
                    }
                }

                if (data.status.Metrics['Memory Usage']) {
                    const memoryValue = data.status.Metrics['Memory Usage'].RawValue;
                    const memoryValueElement = document.getElementById('memory-value');
                    if (memoryValueElement) {
                        memoryValueElement.textContent = `${Math.round(memoryValue)} MB`;
                    }
                }

                if (data.status.Metrics['Active Users']) {
                    const usersValue = data.status.Metrics['Active Users'].RawValue;
                    const usersElement = document.getElementById('users-value');
                    if (usersElement) {
                        usersElement.textContent = usersValue;
                    }
                }

                if (data.status.Uptime !== undefined) {
                    const uptimeValue = data.status.UptimeSeconds || data.status.Uptime;
                    const uptimeElement = document.getElementById('uptime-value');
                    if (uptimeElement) {
                        uptimeElement.textContent = formatUptime(uptimeValue);
                    }
                }
            }

            const lastUpdatedElement = document.getElementById('last-updated');
            if (lastUpdatedElement) {
                lastUpdatedElement.textContent = new Date().toLocaleString();
            }

            pollingInterval = 1000;
            addDebugMessage('info', 'Metrics updated successfully');
        } catch (error) {
            addDebugMessage('error', 'Error processing updates:', error.message);
            console.error('Error processing updates:', error);
        }
    };

    const fetchInstanceUpdates = (forceRefresh = false) => {
        if (pollingTimer) {
            clearTimeout(pollingTimer);
            pollingTimer = null;
        }

        if (!isPolling && !forceRefresh) {
            return;
        }

        const fetchUrl = forceRefresh ? `${apiEndpoint}?refresh=true&_=${Date.now()}` : `${apiEndpoint}?_=${Date.now()}`;

        connectionStatusElement.textContent = 'Fetching...';
        connectionStatusElement.className = 'text-yellow-500';

        updateApiStatus('Fetching...');
        addDebugMessage('info', `Fetching updates from API: ${fetchUrl}`);

        fetch(fetchUrl)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                lastApiResponse = data;

                const unlistedBanner = document.getElementById('amp-instance-unlisted-banner');
                if (unlistedBanner) {
                    if (data.ampUnlisted) {
                        unlistedBanner.classList.remove('hidden');
                    } else {
                        unlistedBanner.classList.add('hidden');
                    }
                }

                const instBanner = document.getElementById('amp-instance-degraded-banner');
                if (instBanner) {
                    const msg = instBanner.querySelector('.js-amp-degraded-message');
                    const stale = instBanner.querySelector('.js-amp-degraded-stale');
                    if (data.ampUnlisted) {
                        instBanner.classList.add('hidden');
                    } else if (data.ampDegraded) {
                        instBanner.classList.remove('hidden');
                        if (msg) {
                            msg.textContent =
                                data.degradedMessage ||
                                'AMP is unreachable — showing the last known snapshot.';
                        }
                        if (stale) {
                            stale.textContent = data.staleDataSavedAt
                                ? 'Snapshot from: ' + data.staleDataSavedAt
                                : '';
                        }
                    } else {
                        instBanner.classList.add('hidden');
                    }
                }

                const rawApiResponse = data._debug?.rawResponse || null;
                const rawDataDisplay = document.getElementById('raw-data-display');
                if (rawDataDisplay && !rawDataDisplay.classList.contains('hidden')) {
                    renderJsonDebugBlock(rawDataDisplay, 'Processed Data:', data);
                }

                const rawApiDisplay = document.getElementById('raw-api-response');
                if (rawApiDisplay && !rawApiDisplay.classList.contains('hidden') && rawApiResponse) {
                    renderJsonDebugBlock(rawApiDisplay, 'Raw API Response:', rawApiResponse);
                }

                addDebugMessage('info', 'API data received', {
                    timestamp: data.timestamp,
                    status: data.status ? 'Present' : 'Missing',
                    metrics: JSON.stringify(data.metrics),
                    cpu: data.metrics?.cpu,
                    memory: data.metrics?.memory,
                    uptime: data.metrics?.uptime,
                    activeUsers: data.metrics?.activeUsers,
                    ampUrl: data._debug?.ampUrl || 'Not available',
                    requestUrl: data._debug?.url || 'Not available',
                });

                connectionStatusElement.textContent = 'Connected';
                connectionStatusElement.className = 'text-green-500';

                updateApiStatus('Connected', data.timestamp);

                if (data._debug?.ampDirectApiUrl) {
                    document.getElementById('amp-direct-url').textContent = data._debug.ampDirectApiUrl;
                } else if (data._debug?.url) {
                    document.getElementById('amp-direct-url').textContent = data._debug.url;
                } else if (data._debug?.fullUrl) {
                    document.getElementById('amp-direct-url').textContent = data._debug.fullUrl;
                } else {
                    document.getElementById('amp-direct-url').textContent =
                        `Debug URL not available: ${data._debug ? 'Debug data exists but missing URL properties' : 'No debug data'}`;
                }

                if (data.sessionToken) {
                    document.getElementById('session-token').textContent = data.sessionToken;
                } else if (data._debug?.tokenFull) {
                    document.getElementById('session-token').textContent = data._debug.tokenFull;
                } else if (data._debug?.sessionToken) {
                    document.getElementById('session-token').textContent = data._debug.sessionToken;
                } else {
                    document.getElementById('session-token').textContent = 'Session token not available in debug data';
                }

                processInstanceUpdates(data);

                if (isPolling) {
                    pollingTimer = setTimeout(fetchInstanceUpdates, pollingInterval);
                }
            })
            .catch((error) => {
                addDebugMessage('error', `API fetch error: ${error.message}`);
                console.error('Error fetching instance updates:', error);

                connectionStatusElement.textContent = 'Connection Error';
                connectionStatusElement.className = 'text-red-500';

                updateApiStatus(`Error: ${error.message}`);

                pollingInterval = Math.min(pollingInterval * 1.5, 10000);

                if (isPolling) {
                    pollingTimer = setTimeout(fetchInstanceUpdates, pollingInterval);
                }
            });
    };

    const rawButton = document.getElementById('btn-show-raw');
    if (rawButton) {
        rawButton.textContent = 'Hide Raw Data';
        rawButton.addEventListener('click', () => {
            const rawDataDisplay = document.getElementById('raw-data-display');
            if (!rawDataDisplay) {
                return;
            }

            rawDataDisplay.classList.toggle('hidden');
            if (!rawDataDisplay.classList.contains('hidden') && lastApiResponse) {
                renderJsonDebugBlock(rawDataDisplay, 'Processed Data:', lastApiResponse);
            }

            rawButton.textContent = rawDataDisplay.classList.contains('hidden') ? 'Show Raw Data' : 'Hide Raw Data';
        });
    }

    const rawApiButton = document.getElementById('btn-show-raw-api');
    if (rawApiButton) {
        rawApiButton.addEventListener('click', () => {
            const rawApiDisplay = document.getElementById('raw-api-response');
            const rawDataDisplay = document.getElementById('raw-data-display');
            if (!rawApiDisplay) {
                return;
            }

            rawApiDisplay.classList.toggle('hidden');

            if (!rawApiDisplay.classList.contains('hidden') && lastApiResponse?._debug?.rawResponse) {
                renderJsonDebugBlock(rawApiDisplay, 'Raw API Response:', lastApiResponse._debug.rawResponse);
            }

            rawApiButton.textContent = rawApiDisplay.classList.contains('hidden') ? 'Show Raw API Response' : 'Hide Raw API Response';

            if (rawDataDisplay && !rawDataDisplay.classList.contains('hidden') && lastApiResponse) {
                renderJsonDebugBlock(rawDataDisplay, 'Processed Data:', lastApiResponse);
            }
        });
    }

    const copyTokenButton = document.getElementById('btn-copy-token');
    if (copyTokenButton) {
        copyTokenButton.addEventListener('click', () => {
            const tokenElement = document.getElementById('session-token');
            const tokenText = tokenElement?.textContent;

            if (tokenText && tokenText !== 'Loading...' && !tokenText.includes('not available')) {
                navigator.clipboard.writeText(tokenText).then(() => {
                    const confirmationElement = document.getElementById('copy-confirmation');
                    confirmationElement?.classList.remove('hidden');
                    setTimeout(() => confirmationElement?.classList.add('hidden'), 2000);
                });
            }
        });
    }

    const debugToggle = document.getElementById('btn-show-debug');
    if (debugToggle) {
        debugToggle.addEventListener('click', () => {
            const apiDebug = document.getElementById('api-debug');
            if (!apiDebug) {
                return;
            }

            apiDebug.style.display = apiDebug.style.display === 'none' ? 'block' : 'none';
            debugToggle.textContent = apiDebug.style.display === 'none' ? 'Show Debug Panel' : 'Hide Debug Panel';
        });
    }

    const logToggle = document.getElementById('toggle-debug-log');
    if (logToggle) {
        logToggle.addEventListener('click', () => {
            const logElement = document.getElementById('api-debug-log');
            if (!logElement) {
                return;
            }

            logElement.classList.toggle('hidden');
            logToggle.textContent = logElement.classList.contains('hidden') ? 'Show Debug Log' : 'Hide Debug Log';
        });
    }

    const pollingToggle = document.getElementById('btn-toggle-polling');
    if (pollingToggle) {
        pollingToggle.addEventListener('click', () => {
            if (isPolling) {
                isPolling = false;
                pollingToggle.textContent = 'Resume Polling';
                pollingToggle.className = 'px-3 py-1 bg-blue-600 text-white rounded text-xs';
                if (pollingTimer) {
                    clearTimeout(pollingTimer);
                    pollingTimer = null;
                }
                connectionStatusElement.textContent = 'Polling Paused';
                connectionStatusElement.className = 'text-yellow-500';
                addDebugMessage('info', 'Polling paused by user');
            } else {
                isPolling = true;
                pollingToggle.textContent = 'Pause Polling';
                pollingToggle.className = 'px-3 py-1 bg-yellow-600 text-white rounded text-xs';
                fetchInstanceUpdates();
                addDebugMessage('info', 'Polling resumed by user');
            }
        });
    }

    const forceRefresh = document.getElementById('btn-force-refresh');
    if (forceRefresh) {
        forceRefresh.addEventListener('click', () => {
            addDebugMessage('info', 'Manual refresh requested');
            fetchInstanceUpdates(true);
        });
    }

    fetchInstanceUpdates();

    window.addEventListener('beforeunload', () => {
        if (pollingTimer) {
            clearTimeout(pollingTimer);
        }
    });
};

document.addEventListener('DOMContentLoaded', () => {
    initAmpMonitoringDashboard();
    initAmpMonitoringInstance();
});
